import React from 'react';
import PropTypes from 'prop-types';
import CardContent from '@material-ui/core/CardContent';
import Typography from '@material-ui/core/Typography';
import TextOnlyFormField from '../TextOnlyFormField/TextOnlyFormField';
import dateformat from 'dateformat';
import orderBy from 'lodash/orderBy';
import { SketchOrderStatus } from '../../lib/sketchOrderStatuses';
import { SketchOptions } from '../../lib/sketchOptions';
import TextField from '@material-ui/core/TextField';
import FormControl from '@material-ui/core/FormControl';
import FormControlLabel from '@material-ui/core/FormControlLabel';
import FormHelperText from '@material-ui/core/FormHelperText';
import Checkbox from '@material-ui/core/Checkbox';
import InputLabel from '@material-ui/core/InputLabel';
import Input from '@material-ui/core/Input';
import InputAdornment from '@material-ui/core/InputAdornment';
import Select from '@material-ui/core/Select';
import MenuItem from '@material-ui/core/MenuItem';
import CurrencyNumberFormatter from '../CurrencyNumberFormatter';
import { withStyles } from '@material-ui/core/styles';

const style = () => ({
    formField: {
        marginBottom: 12,
    },
});

const SketchOrderCardDetails = (props) => {
    const shouldPricingInformationBeShown = () => {
        const isInProperStatus =
            props.sketchOrder.sketchOrderStatus ===
                SketchOrderStatus.PendingReview ||
            props.sketchOrder.sketchOrderStatus === SketchOrderStatus.Complete;

        return isInProperStatus && props.showCost;
    };

    const renderTierOptions = (tier) => {
        const { sketchOrderTierDefinition } = tier;

        return (
            <MenuItem key={tier.id} value={tier.id}>
                {tier.name} &nbsp;
                {sketchOrderTierDefinition.sketchOrderTierDefinition !==
                    'HalfSnap' &&
                    sketchOrderTierDefinition.sketchOrderTierDefinition !==
                        'Gutters' &&
                    `(${sketchOrderTierDefinition.minimumValue} - ${sketchOrderTierDefinition.maximumValue}
                    ${sketchOrderTierDefinition.sketchOrderTierDefinition})`}
            </MenuItem>
        );
    };

    const orderedTiers = orderBy(props.sketchOrderTiers, ['id']);
    return (
        <CardContent>
            <Typography variant='h6' gutterBottom>
                Details
            </Typography>
            <FormControl fullWidth className={props.classes.formField}>
                <InputLabel htmlFor='sketch-order-internal-notes'>
                    Internal Notes
                </InputLabel>
                <Input
                    value={props.sketchOrder.internalNotes || ''}
                    onChange={props.onSketchOrderChange}
                    multiline
                    inputProps={{
                        name: 'internalNotes',
                        id: 'sketch-order-internal-notes',
                        readOnly: !props.canEditInternalNotes,
                    }}
                />
            </FormControl>
            {props.sketchOrder.rushed && (
                <TextOnlyFormField label='Delivery Type' value='Rush' />
            )}
            <TextOnlyFormField
                label='Requested'
                value={dateformat(
                    props.sketchOrder.requestedDate,
                    'mmm d, yyyy h:MM TT',
                )}
            />
            {props.sketchOrder.completedDate && (
                <TextOnlyFormField
                    label='Completed'
                    value={dateformat(
                        props.sketchOrder.completedDate,
                        'mmm d, yyyy h:MM TT',
                    )}
                />
            )}
            {props.sketchOrder.sketchOptions && (
                <FormControl className={props.classes.formField}>
                    <Typography variant='body1'>Sketch Options:</Typography>
                    {props.sketchOrder.sketchOptions.map((option, index) => {
                        return (
                            <Typography key={index} variant='body1'>
                                {SketchOptions[option]}
                            </Typography>
                        );
                    })}
                </FormControl>
            )}
            <TextOnlyFormField
                label='Customer Notes'
                value={props.sketchOrder.notes}
            />
            {props.showProjectName && (
                <TextField
                    label='Project Name'
                    name='projectName'
                    onChange={props.onSketchOrderChange}
                    value={props.sketchOrder.projectName || ''}
                    fullWidth
                    className={props.classes.formField}
                    inputProps={{
                        readOnly: !props.canEditProjectName,
                    }}
                />
            )}
            {props.showReportType && (
                <FormControl fullWidth className={props.classes.formField}>
                    <InputLabel htmlFor='sketch-report-type'>
                        Report Type
                    </InputLabel>
                    <Select
                        value={props.sketchOrder.sketchReportType || ''}
                        onChange={props.onReportTypeChange}
                        inputProps={{
                            name: 'sketchReportType',
                            id: 'sketch-report-type',
                            readOnly: !props.canEditReportType,
                        }}
                    >
                        <MenuItem value='FullSnap'>FullSnap</MenuItem>
                        <MenuItem value='HalfSnap'>HalfSnap</MenuItem>
                        <MenuItem value='MetalRoof'>MetalRoof</MenuItem>
                        <MenuItem value='Gutters'>Gutters</MenuItem>
                        <MenuItem value='LightingReport'>LightingReport</MenuItem>
                    </Select>
                </FormControl>
            )}
            {props.sketchOrder.metalRoofPanelSize > 0 && (
                <TextOnlyFormField
                    label='Metal Panel Size'
                    value={props.sketchOrder.metalRoofPanelSize + ' in'}
                />
            )}
            {props.projectDrawingSummary.numberOfFacets > 0 && (
                <TextOnlyFormField
                    label='Number of Facets'
                    value={props.projectDrawingSummary.numberOfFacets}
                />
            )}          
            {props.projectDrawingSummary.actualSquares > 0 && (
                <TextOnlyFormField
                    label='Actual Squares'
                    value={props.projectDrawingSummary.actualSquares}
                />
            )}
            {shouldPricingInformationBeShown() && (
                <div>
                    {props.sketchOrder.sketchOrderTierId && (
                        <FormControl
                            fullWidth
                            className={props.classes.formField}
                            disabled={props.reportTypeChanged}
                        >
                            <InputLabel htmlFor='sketch-order-tier'>
                                Tier
                            </InputLabel>
                            <Select
                                value={props.sketchOrder.sketchOrderTierId}
                                inputProps={{
                                    name: 'sketchOrderTierId',
                                    id: 'sketch-order-tier',
                                    readOnly: !props.canEditTier,
                                }}
                                onChange={props.onSketchOrderChange}
                            >
                                {orderedTiers.map(renderTierOptions)}
                            </Select>
                        </FormControl>
                    )}
                    <FormControl
                        fullWidth
                        className={props.classes.formField}
                        disabled={props.reportTypeChanged}
                    >
                        <InputLabel htmlFor='sketch-order-cost'>
                            Cost
                        </InputLabel>
                        <Input
                            value={props.sketchOrder.cost}
                            onChange={props.onSketchOrderChange}
                            inputComponent={CurrencyNumberFormatter}
                            inputProps={{
                                name: 'cost',
                                id: 'sketch-order-cost',
                                readOnly: !props.canEditCost,
                            }}
                            startAdornment={
                                <InputAdornment position='start'>
                                    $
                                </InputAdornment>
                            }
                        />
                        {props.reportTypeChanged && (
                            <FormHelperText>
                                Report type changed. Cost and tier will be
                                recalculated on update.
                            </FormHelperText>
                        )}
                    </FormControl>
                    <FormControlLabel
                        control={
                            <Checkbox
                                checked={props.sketchOrder.freeOrder}
                                onChange={props.onFreeOrderChange}
                                value={props.sketchOrder.freeOrder}
                                name='freeOrder'
                                disabled={!props.canEditFreeOrder}
                            />
                        }
                        label='Make sketch order free'
                    />
                </div>
            )}
        </CardContent>
    );
};

SketchOrderCardDetails.propTypes = {
    sketchOrder: PropTypes.shape({
        requestedDate: PropTypes.string,
        completedDate: PropTypes.string,
        projectName: PropTypes.string,
        sketchOrderStatus: PropTypes.string,
        sketchReportType: PropTypes.string,
        sketchOrderTierId: PropTypes.number,
        metalRoofPanelSize: PropTypes.number,
        cost: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
        notes: PropTypes.string,
        sketchOptions: PropTypes.arrayOf(PropTypes.string),
        freeOrder: PropTypes.bool,
    }).isRequired,
    onSketchOrderChange: PropTypes.func.isRequired,
    sketchOrderTiers: PropTypes.arrayOf(
        PropTypes.shape({
            id: PropTypes.number,
            name: PropTypes.string,
            sketchOrderTierDefinition: PropTypes.shape({
                minimumValue: PropTypes.number.isRequired,
                maximumValue: PropTypes.number.isRequired,
                sketchOrderTierDefinition: PropTypes.string.isRequired,
            }).isRequired,
        }),
    ).isRequired,
    projectDrawingSummary: PropTypes.shape({
        numberOfFacets: PropTypes.number,
        actualSquares: PropTypes.number,
    }).isRequired,
    classes: PropTypes.shape({
        formField: PropTypes.string.isRequired,
    }).isRequired,
    showProjectName: PropTypes.bool,
    showCost: PropTypes.bool,
    showLinksToSupportDashboard: PropTypes.bool,
    showReportType: PropTypes.bool,
    canEditInternalNotes: PropTypes.bool,
    canEditProjectName: PropTypes.bool,
    canEditReportType: PropTypes.bool,
    canEditCost: PropTypes.bool,
    canEditTier: PropTypes.bool,
    canEditFreeOrder: PropTypes.bool,
    onFreeOrderChange: PropTypes.func.isRequired,
    reportTypeChanged: PropTypes.bool,
    onReportTypeChange: PropTypes.func.isRequired,
};

SketchOrderCardDetails.defaultProps = {
    showProjectName: false,
    showCost: false,
    showLinksToSupportDashboard: false,
    showReportType: false,
    canEditProjectName: false,
    canEditReportType: false,
    canEditCost: false,
    canEditTier: false,
    canEditInternalNotes: false,
    canEditFreeOrder: false,
    reportTypeChange: false,
};

export default withStyles(style)(SketchOrderCardDetails);
